import { locales, Locale } from "@/i18n/config";

/**
 * Build a locale-name formatter for one UI locale. Returns full
 * "<language> (<region>)" names, OVERRIDING the handful of CLDR codes that
 * otherwise resolve to a special standalone name — e.g. de-CH →
 * "Schweizer Hochdeutsch", nl-BE → "Flämisch", en-GB → "British English".
 * Those special names sort away from their language family (de-CH under "S",
 * nl-BE under "F") and read as "missing" in the language pickers. Normalising
 * every region-bearing code to "<language> (<region>)" makes each variant group
 * and sort with its siblings, matching the hand-translated guest-language field.
 *
 * Used by every Intl-based language picker (mail templates, all 4 builders,
 * portal, registration) so the displayed AND round-tripped names stay identical
 * across the code→name and name→code directions.
 */
export function makeLocaleNamer(
  currentLocale: string,
): (code: string) => string {
  const langNames = new Intl.DisplayNames([currentLocale], {
    type: "language",
  });
  const regionNames = new Intl.DisplayNames([currentLocale], {
    type: "region",
  });
  // NEVER throws (PB-FB-H3-MAILNOLANG): `Intl.DisplayNames.of()` raises a
  // RangeError for `undefined`, `""` and every structurally invalid tag, and
  // these codes come from stored documents — a mail template without
  // `primaryLanguage` crashed the whole mail builder through its language
  // dropdown. A missing code names nothing (""), an invalid one names itself.
  return (code: string) => {
    if (typeof code !== "string" || code === "") return "";
    let raw: string;
    try {
      raw = langNames.of(code) ?? code;
    } catch {
      return code;
    }
    // Already "Language (Region)", or no region subtag to add → keep as-is.
    if (raw.includes("(") || !code.includes("-")) return raw;
    const [lang, region] = code.split("-");
    try {
      const base = langNames.of(lang);
      const regionName = regionNames.of(region);
      return base && regionName ? `${base} (${regionName})` : raw;
    } catch {
      return raw;
    }
  };
}

/**
 * Returns a sorted list of { locale, name } pairs for all supported app locales,
 * localized using the browser's built-in Intl.DisplayNames API.
 */
export function getLocalizedLanguages(
  currentLocale: string,
): { locale: Locale; name: string }[] {
  const nameOf = makeLocaleNamer(currentLocale);

  return [...locales]
    .map((loc) => ({
      locale: loc,
      name: nameOf(loc),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, currentLocale));
}

/**
 * Returns a sorted list of display names for use in OptionInput.
 */
export function getLanguageDisplayNames(currentLocale: string): string[] {
  return getLocalizedLanguages(currentLocale).map((l) => l.name);
}

/**
 * Resolves a language display name back to its locale code.
 */
export function getLocaleFromDisplayName(
  displayName: string,
  currentLocale: string,
): Locale | undefined {
  const languages = getLocalizedLanguages(currentLocale);
  return languages.find((l) => l.name === displayName)?.locale;
}

/**
 * Resolves a locale code to its localized display name.
 */
/**
 * Like {@link getDisplayNameFromLocale} — which, since PB-FB-H3-MAILNOLANG, no
 * longer throws either (the guard sits in {@link makeLocaleNamer}); kept as the
 * explicit name for call sites that pass unvalidated values.
 *
 * `Intl.DisplayNames.of()` raises a RangeError on a structurally invalid tag,
 * and these names are now built from values that never passed a validator: guest
 * `language` fields (the public `/data-input/submit` door and the check-in app
 * both store free text) and template content-map KEYS, which are ordinary
 * database strings. A throw in a send dialog has no local ErrorBoundary — the
 * app-level one would replace the whole page. Falling back to the raw tag keeps
 * the surface honest and standing.
 */
export function safeLanguageName(code: string, currentLocale: string): string {
  if (!code) return "";
  try {
    return getDisplayNameFromLocale(code as Locale, currentLocale);
  } catch {
    return code;
  }
}

export function getDisplayNameFromLocale(
  locale: Locale,
  currentLocale: string,
): string {
  return makeLocaleNamer(currentLocale)(locale);
}

/**
 * Build sorted { code, name } pairs for an arbitrary list of locale codes
 * (e.g. ALL_LANGUAGE_OPTIONS), localized via Intl.DisplayNames. Used by the
 * template-language pickers so they show full locales with proper names —
 * the same mechanism the guest language field uses — instead of the legacy
 * `htmlLanguages` i18n namespace.
 */
export function getLocaleOptions(
  codes: readonly string[],
  currentLocale: string,
): { code: string; name: string }[] {
  const nameOf = makeLocaleNamer(currentLocale);
  return codes
    .map((code) => ({ code, name: nameOf(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, currentLocale));
}

/**
 * Like getLocaleOptions but also includes any `extra` codes not already in the
 * canonical list. Used by the language pickers so a template that still holds a
 * legacy short code (e.g. "en" on a pre-migration template) keeps showing —
 * and round-tripping — as a selectable option instead of being dropped.
 */
export function getLocaleOptionsWith(
  codes: readonly string[],
  extra: readonly (string | undefined | null)[],
  currentLocale: string,
): { code: string; name: string }[] {
  const seen = new Set(codes);
  const merged = [...codes];
  for (const c of extra) {
    if (typeof c === "string" && c.length > 0 && !seen.has(c)) {
      seen.add(c);
      merged.push(c);
    }
  }
  return getLocaleOptions(merged, currentLocale);
}
